import clipping from 'polygon-clipping';
import { WALL_THICKNESS } from './constants.js';

const EPS = 1e-6;
export const isStructuralWall = w => !w.roofPts && w.kind !== 'glass' && (!w.objectKind || w.objectKind === 'wall-joint');
export function wallFootprint(w) {
  if (w.roofPts) return [w.roofPts, ...(w.roofHoles || [])].map(r => r.map(p => [p.x, p.y]));
  if (w.wallPts) return [w.wallPts.map(p => [p.x, p.y])];
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1, length = Math.hypot(dx, dy);
  if (length < EPS) return [];
  const t = (w.thickness ?? WALL_THICKNESS) / 2, nx = -dy / length * t, ny = dx / length * t;
  return [[[w.x1 + nx, w.y1 + ny], [w.x1 - nx, w.y1 - ny], [w.x2 - nx, w.y2 - ny], [w.x2 + nx, w.y2 + ny]]];
}

/** Fill only the outside wedge of actual connected wall ends. T/X junctions
 * already overlap; a doorway or two nearby, disconnected ends is never joined.
 * Height bands preserve low walls, window sills and headers. The extra prisms
 * are shared by coverage, collision, lighting and the welded render shell. */
export function wallJointPrisms(obstructions) {
  const walls = obstructions.filter(w => isStructuralWall(w) && !w.wallPts && w.h > (w.bottom || 0));
  const nodes = new Map(), out = [];
  for (const w of walls) for (const [x, y] of [[w.x1, w.y1], [w.x2, w.y2]]) {
    const key = `${Math.round(x / EPS)},${Math.round(y / EPS)}`;
    if (!nodes.has(key)) nodes.set(key, { x, y });
  }
  for (const [key, p] of nodes) {
    const incident = [];
    for (const w of walls) {
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1, length = Math.hypot(dx, dy);
      if (length < EPS) continue;
      const ux = dx / length, uy = dy / length, along = (p.x - w.x1) * ux + (p.y - w.y1) * uy;
      if (along < -EPS || along > length + EPS || Math.abs((p.x - w.x1) * uy - (p.y - w.y1) * ux) > EPS) continue;
      if (along > EPS) incident.push({ w, ux: -ux, uy: -uy, length: along });
      if (along < length - EPS) incident.push({ w, ux, uy, length: length - along });
    }
    if (incident.length < 2) continue;
    const heights = [...new Set(incident.flatMap(r => [r.w.bottom || 0, r.w.h]))].sort((a, b) => a - b);
    for (let h = 1; h < heights.length; h++) {
      const bottom = heights[h - 1], top = heights[h], mid = (bottom + top) / 2;
      const active = incident.filter(r => (r.w.bottom || 0) < mid && r.w.h > mid);
      const rays = [];
      for (const r of active) {
        const angle = (Math.atan2(r.uy, r.ux) + Math.PI * 2) % (Math.PI * 2), t = (r.w.thickness ?? WALL_THICKNESS) / 2;
        const existing = rays.find(q => Math.abs(q.ux - r.ux) < EPS && Math.abs(q.uy - r.uy) < EPS);
        if (existing) { if (t > existing.t) Object.assign(existing, r, { angle, t }); }
        else rays.push({ ...r, angle, t });
      }
      if (rays.length < 2) continue;
      rays.sort((a, b) => a.angle - b.angle);
      for (let i = 0; i < rays.length; i++) {
        const a = rays[i], b = rays[(i + 1) % rays.length];
        const gap = (b.angle - a.angle + Math.PI * 2) % (Math.PI * 2);
        if (gap <= Math.PI + EPS) continue;
        // Left edge of a and right edge of b enclose the outside corner.
        const pa = [p.x - a.uy * a.t, p.y + a.ux * a.t], pb = [p.x + b.uy * b.t, p.y - b.ux * b.t];
        const den = a.ux * b.uy - a.uy * b.ux;
        if (Math.abs(den) < EPS) continue;
        const u = ((pb[0] - pa[0]) * b.uy - (pb[1] - pa[1]) * b.ux) / den;
        const corner = [pa[0] + a.ux * u, pa[1] + a.uy * u];
        // Bevel very acute joins instead of producing metre-long spikes.
        const limit = Math.min(4 * Math.max(a.t, b.t), a.length / 2, b.length / 2);
        const points = [[p.x, p.y], pa, ...(Math.hypot(corner[0] - p.x, corner[1] - p.y) <= limit + EPS ? [corner] : []), pb];
        const pieces = clipping.difference([points], ...active.map(r => wallFootprint(r.w)));
        for (const rings of pieces) {
          const wallPts = rings[0].slice(0, -1).map(([x, y]) => ({ x, y }));
          if (wallPts.length < 3) continue;
          out.push({ id: `joint:${key}:${h}:${i}:${out.length}`, sourceId: a.w.sourceId || a.w.id,
            kind: 'solid', objectKind: 'wall-joint', mountRule: 'prohibited', color: a.w.color,
            wallPts, bottom, h: top, thickness: 0,
            x1: Math.min(...wallPts.map(p => p.x)), y1: Math.min(...wallPts.map(p => p.y)),
            x2: Math.max(...wallPts.map(p => p.x)), y2: Math.max(...wallPts.map(p => p.y)) });
        }
      }
    }
  }
  return out;
}
